import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Layout, Card, Table, Row, Col, Button, Tag, Typography, Modal, Form, Input, Space, Flex, Popconfirm, Avatar, message, Select, Segmented, Badge, Spin } from "antd";
import { CheckOutlined, CloseOutlined, UserOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, PhoneOutlined, MailOutlined, BankOutlined, DownloadOutlined, EyeOutlined, CalendarOutlined } from "@ant-design/icons";
import InrOutlined from "../../general/components/common/InrOutlined";
import { superAdminService } from "../services/superAdminService";
import TopNavbar from "../../general/components/top-bar/TopNavbar";
import Sidebar from "../../general/components/left-menu/Sidebar";
import CompanyDossierDrawer from "../components/CompanyDossierDrawer";
import PlatformAnalyticsView from "../components/PlatformAnalyticsView";
import GlobalPaymentsView from "../components/GlobalPaymentsView";
import BottomNavigation from "../../general/components/bottom-navigation/BottomNavigation";

const { Content } = Layout;
const { Title, Text } = Typography;

const isSameDay = (d, now) => d.toDateString() === now.toDateString();

const isThisWeek = (d, now) => {
  const day = now.getDay();
  const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + (day === 0 ? -6 : 1));
  return d >= startOfWeek && d <= now;
};

const isThisMonth = (d, now) => d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();

const isLastMonth = (d, now) => {
  const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return d.getFullYear() === year && d.getMonth() === lastMonth;
};

const isThisQuarter = (d, now) => {
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const itemQuarter = Math.floor(d.getMonth() / 3);
  return d.getFullYear() === now.getFullYear() && currentQuarter === itemQuarter;
};

const isThisYear = (d, now) => d.getFullYear() === now.getFullYear();

const PERIOD_CHECKERS = {
  today: isSameDay,
  this_week: isThisWeek,
  this_month: isThisMonth,
  last_month: isLastMonth,
  this_quarter: isThisQuarter,
  this_year: isThisYear,
};

/**
 * Check if a date string falls into the requested period
 */
function isDateInPeriod(dateStr, period) {
  if (!period || period === "all") return true;
  if (!dateStr) return false;
  const checker = PERIOD_CHECKERS[period];
  if (!checker) return true;
  const d = new Date(dateStr);
  const now = new Date();
  return checker(d, now);
}

function matchesCompanySearch(c, searchQuery) {
  if (!searchQuery?.trim()) return true;
  const q = searchQuery.toLowerCase();
  const cName = String(c.companyName || "").toLowerCase();
  const cCode = String(c.companyCode || "").toLowerCase();
  const aName = String(c.name || "").toLowerCase();
  const aEmail = String(c.email || "").toLowerCase();
  const aMobile = String(c.mobile || "").toLowerCase();
  const cStatus = String(c.status || "").toLowerCase();
  return (
    cName.includes(q) ||
    cCode.includes(q) ||
    aName.includes(q) ||
    aEmail.includes(q) ||
    aMobile.includes(q) ||
    cStatus.includes(q)
  );
}

const STATUS_TAG_MAP = {
  "Free-Trial-Request": { color: "purple", label: "Trial Request" },
  "Free-Trial": { color: "success", label: "Free Trial" },
  "On due": { color: "error", label: "On Due" },
};

function StatusTag({ status }) {
  const config = STATUS_TAG_MAP[status] || { color: "default", label: status || "Active" };
  return (
    <Tag color={config.color} style={{ borderRadius: 6, fontWeight: 500 }}>
      {config.label}
    </Tag>
  );
}

function CompanyCell({ record, onClick, avatarBg = "#2459d3" }) {
  return (
    <Flex
      align="center"
      gap={10}
      style={{ cursor: "pointer" }}
      onClick={() => onClick(record.companyCode)}
    >
      <Avatar
        style={{
          backgroundColor: avatarBg,
          fontWeight: 600,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {(record.companyName || "CO").substring(0, 2).toUpperCase()}
      </Avatar>
      <Flex vertical gap={2}>
        <Text strong style={{ color: "#0f172a", fontSize: "0.9rem" }}>
          {record.companyName}
        </Text>
        <Text type="secondary" style={{ fontSize: "0.75rem", fontFamily: "monospace" }}>
          {record.companyCode || "—"}
        </Text>
      </Flex>
    </Flex>
  );
}

function AdminContactCell({ record }) {
  return (
    <Flex vertical gap={2}>
      <Text style={{ fontWeight: 600, color: "#334155", fontSize: "0.85rem" }}>{record.name || "—"}</Text>
      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
        {record.email || "—"}
      </Text>
      {record.mobile && (
        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
          {record.countryCode || "+91"} {record.mobile}
        </Text>
      )}
    </Flex>
  );
}

function PeriodFilterDropdown({ value, onChange }) {
  return (
    <Flex align="center" gap={6}>
      <CalendarOutlined style={{ color: "#7c3aed", fontSize: "0.85rem" }} />
      <Text type="secondary" style={{ fontSize: "0.82rem" }}>
        Registered:
      </Text>
      <Select
        value={value}
        onChange={onChange}
        style={{ width: 140 }}
        options={[
          { value: "all", label: "All Time" },
          { value: "today", label: "Today" },
          { value: "this_week", label: "This Week" },
          { value: "this_month", label: "This Month" },
          { value: "last_month", label: "Last Month" },
          { value: "this_quarter", label: "This Quarter" },
          { value: "this_year", label: "This Year" },
        ]}
      />
    </Flex>
  );
}

function exportCompaniesToCsv(filteredCompanies) {
  if (filteredCompanies.length === 0) {
    message.warning("No companies to export.");
    return;
  }

  const headers = [
    "Company Name",
    "Company Code",
    "Admin Name",
    "Email",
    "Phone",
    "Industry",
    "Team Size",
    "Status",
    "Registered At",
    "Trial Start",
    "Trial End",
    "Assigned RM",
  ];

  const rows = filteredCompanies.map((c) => [
    `"${c.companyName || ""}"`,
    `"${c.companyCode || ""}"`,
    `"${c.name || ""}"`,
    `"${c.email || ""}"`,
    `"${c.mobile || ""}"`,
    `"${c.industry || "General"}"`,
    `"${c.teamSize || "1-10"}"`,
    `"${c.status || "Free-Trial"}"`,
    `"${c.createdAt ? new Date(c.createdAt).toISOString() : ""}"`,
    `"${c.trialStartDate ? new Date(c.trialStartDate).toISOString() : ""}"`,
    `"${c.trialEndDate ? new Date(c.trialEndDate).toISOString() : ""}"`,
    `"${c.relationshipManager?.name || "Unassigned"}"`,
  ]);

  const csvContent =
    "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `DealVoice_Enterprises_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  message.success("Enterprise directory exported to CSV!");
}

function getStatsCardsConfig(activeTab, stats, rmStats, pendingTrialRequests, pendingRmRequests) {
  if (activeTab === "requests") {
    return [
      {
        title: "Total Inbound Requests",
        value: pendingTrialRequests.length + pendingRmRequests.length,
        icon: <ClockCircleOutlined />,
        color: "#334155",
        bg: "#f1f5f9",
      },
      {
        title: "Trial Requests",
        value: pendingTrialRequests.length,
        icon: <ClockCircleOutlined />,
        color: "#7c3aed",
        bg: "#f5f3ff",
      },
      {
        title: "Pending RM Requests",
        value: pendingRmRequests.length,
        icon: <UserOutlined />,
        color: "#f59e0b",
        bg: "#fffbeb",
      },
      {
        title: "Total Tenants",
        value: stats.total,
        icon: <BankOutlined />,
        color: "#334155",
        bg: "#f1f5f9",
      },
    ];
  }
  if (activeTab === "rm") {
    return [
      {
        title: "Total Registered",
        value: stats.total,
        icon: <BankOutlined />,
        color: "#334155",
        bg: "#f1f5f9",
      },
      {
        title: "Pending RM Requests",
        value: rmStats.pending,
        icon: <UserOutlined />,
        color: "#f59e0b",
        bg: "#fffbeb",
      },
      {
        title: "Assigned RMs",
        value: rmStats.assigned,
        icon: <CheckCircleOutlined />,
        color: "#10b981",
        bg: "#ecfdf5",
      },
      {
        title: "Unassigned Accounts",
        value: rmStats.unassigned,
        icon: <UserOutlined />,
        color: "#64748b",
        bg: "#f1f5f9",
      },
    ];
  }
  return [
    {
      title: "Total Registered",
      value: stats.total,
      icon: <BankOutlined />,
      color: "#334155",
      bg: "#f1f5f9",
    },
    {
      title: "Pending Requests",
      value: stats.pending,
      icon: <ClockCircleOutlined />,
      color: "#7c3aed",
      bg: "#f5f3ff",
    },
    {
      title: "Active Trials",
      value: stats.active,
      icon: <CheckCircleOutlined />,
      color: "#10b981",
      bg: "#ecfdf5",
    },
    {
      title: "On Due Accounts",
      value: stats.due,
      icon: <ExclamationCircleOutlined />,
      color: "#ef4444",
      bg: "#fef2f2",
    },
  ];
}

function SuperAdminStatsStrip({ activeTab, stats, rmStats, pendingTrialRequests, pendingRmRequests }) {
  const cards = getStatsCardsConfig(activeTab, stats, rmStats, pendingTrialRequests, pendingRmRequests);
  return (
    <Row gutter={[16, 16]}>
      {cards.map((c) => (
        <Col xs={24} sm={12} md={6} key={c.title}>
          <Card
            bordered={false}
            styles={{ body: { padding: "16px 20px" } }}
            style={{
              borderRadius: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              background: "#ffffff",
            }}
          >
            <Flex justify="space-between" align="center">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: "0.82rem", fontWeight: 500, color: c.color }}>
                  {c.title}
                </Text>
                <Title level={2} style={{ margin: 0, color: c.color === "#334155" ? "#0f172a" : c.color }}>
                  {c.value}
                </Title>
              </Flex>
              <Avatar
                size={44}
                icon={c.icon}
                style={{ backgroundColor: c.bg, color: c.color }}
              />
            </Flex>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function buildRequestColumns({ onOpenDossier, onApproveCompany, onRejectCompany }) {
  return [
    {
      title: "Company",
      key: "company",
      width: 240,
      render: (_, record) => <CompanyCell record={record} onClick={onOpenDossier} avatarBg="#7c3aed" />,
    },
    {
      title: "Admin",
      key: "admin",
      width: 220,
      render: (_, record) => <AdminContactCell record={record} />,
    },
    {
      title: "Current Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (dt) => (
        <Text style={{ color: "#475569", fontSize: "0.82rem" }}>
          {dt ? new Date(dt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 220,
      render: (_, record) => {
        if (record.status === "Free-Trial-Request") {
          return (
            <Space size={8}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onOpenDossier(record.companyCode)}
                style={{ borderRadius: 6 }}
              >
                Inspect
              </Button>

              <Popconfirm
                title="Enable 7-day trial?"
                description={`Approve 7-day free trial for ${record.companyName}?`}
                onConfirm={() => onApproveCompany(record._id)}
                okText="Approve"
                cancelText="Cancel"
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  style={{
                    borderRadius: 6,
                    backgroundColor: "#10b981",
                    borderColor: "#10b981",
                    fontWeight: 500,
                  }}
                >
                  Approve
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Reject request?"
                description={`Are you sure you want to reject and remove ${record.companyName}?`}
                onConfirm={() => onRejectCompany(record._id)}
                okText="Reject"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button danger size="small" icon={<CloseOutlined />} style={{ borderRadius: 6, fontWeight: 500 }}>
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Button size="small" icon={<EyeOutlined />} onClick={() => onOpenDossier(record.companyCode)}>
            Dossier
          </Button>
        );
      },
    },
  ];
}

function buildCompanyColumns({ onOpenDossier, onOpenPaymentsModal }) {
  return [
    {
      title: "Company",
      key: "company",
      width: 240,
      render: (_, record) => <CompanyCell record={record} onClick={onOpenDossier} avatarBg="#2459d3" />,
    },
    {
      title: "Admin",
      key: "admin",
      width: 220,
      render: (_, record) => <AdminContactCell record={record} />,
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
      width: 140,
      render: (ind) => <Text style={{ color: "#475569", fontSize: "0.85rem" }}>{ind || "—"}</Text>,
    },
    {
      title: "Team Size",
      dataIndex: "teamSize",
      key: "teamSize",
      width: 120,
      render: (size) => <Text style={{ color: "#475569", fontSize: "0.85rem" }}>{size || "—"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 190,
      render: (_, record) => (
        <Space size={6}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onOpenDossier(record.companyCode)}
            style={{ borderRadius: 6 }}
          >
            Dossier
          </Button>

          <Button
            size="small"
            icon={<InrOutlined />}
            onClick={() => onOpenPaymentsModal(record)}
            style={{
              borderRadius: 6,
              fontWeight: 500,
              fontSize: "0.78rem",
              color: "#2459d3",
              borderColor: "#bfdbfe",
              background: "#eff6ff",
            }}
          >
            Payments
          </Button>
        </Space>
      ),
    },
  ];
}

function buildRmRequestColumns({ onOpenDossier, onOpenRmModal }) {
  return [
    {
      title: "Company",
      key: "company",
      width: 240,
      render: (_, record) => <CompanyCell record={record} onClick={onOpenDossier} avatarBg="#f59e0b" />,
    },
    {
      title: "Admin Contact",
      key: "admin",
      width: 220,
      render: (_, record) => <AdminContactCell record={record} />,
    },
    {
      title: "Requested On",
      dataIndex: "rmRequestTime",
      key: "rmRequestTime",
      width: 160,
      render: (dt) => (
        <Flex vertical gap={2}>
          <Text style={{ color: "#475569", fontSize: "0.82rem", fontWeight: 500 }}>
            {dt ? new Date(dt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {dt ? new Date(dt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : ""}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 160,
      render: () => (
        <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600 }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          Awaiting RM
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 200,
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onOpenDossier(record.companyCode)}
            style={{ borderRadius: 6 }}
          >
            Dossier
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<UserOutlined />}
            onClick={() => onOpenRmModal(record)}
            style={{ borderRadius: 6, backgroundColor: "#7c3aed" }}
          >
            Assign RM
          </Button>
        </Space>
      ),
    },
  ];
}

function buildRmColumns({ onOpenDossier, onOpenRmModal }) {
  return [
    {
      title: "Company",
      key: "company",
      width: 240,
      render: (_, record) => <CompanyCell record={record} onClick={onOpenDossier} avatarBg="#f59e0b" />,
    },
    {
      title: "RM Requested?",
      key: "rmRequested",
      width: 170,
      render: (_, record) => {
        const hasRm = Boolean(record.relationshipManager?.name);
        if (record.rmRequestTime && !hasRm) {
          return (
            <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Requested (Pending)
            </Tag>
          );
        }
        if (hasRm) {
          return (
            <Tag color="green" style={{ borderRadius: 6, fontWeight: 500 }}>
              <CheckCircleOutlined style={{ marginRight: 4 }} />
              Assigned
            </Tag>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: "0.82rem" }}>
            Not Requested
          </Text>
        );
      },
    },
    {
      title: "Current RM",
      key: "currentRm",
      width: 220,
      render: (_, record) => {
        const hasRm = record.relationshipManager?.name;
        if (hasRm) {
          return (
            <Flex vertical gap={2}>
              <Text strong style={{ color: "#1e293b", fontSize: "0.85rem" }}>
                {record.relationshipManager.name}
              </Text>
              <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                {record.relationshipManager.phone || "—"}
              </Text>
            </Flex>
          );
        }
        return (
          <Text type="secondary" style={{ fontStyle: "italic", fontSize: "0.82rem" }}>
            Unassigned
          </Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        const hasRm = Boolean(record.relationshipManager?.name);
        const isPending = record.rmRequestTime && !hasRm;
        return (
          <Space size={6}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onOpenDossier(record.companyCode)}
              style={{ borderRadius: 6 }}
            >
              Dossier
            </Button>

            <Button
              type="primary"
              size="small"
              icon={<UserOutlined />}
              onClick={() => onOpenRmModal(record)}
              style={{
                borderRadius: 6,
                backgroundColor: isPending ? "#7c3aed" : undefined,
              }}
            >
              {hasRm ? "Reassign" : "Assign RM"}
            </Button>
          </Space>
        );
      },
    },
  ];
}

const PAYMENT_COLUMNS = [
  {
    title: "Created",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 120,
    render: (dt) => (
      <Text style={{ fontSize: "0.82rem" }}>
        {dt ? new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
      </Text>
    ),
  },
  {
    title: "Period",
    key: "period",
    width: 170,
    render: (_, p) => (
      <Flex vertical gap={2}>
        <Text strong style={{ fontSize: "0.82rem" }}>
          {p.fromDate ? new Date(p.fromDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
        </Text>
        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
          → {p.toDate ? new Date(p.toDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"} ({p.days || 0}d)
        </Text>
      </Flex>
    ),
  },
  {
    title: "Emp",
    dataIndex: "teamSizeMax",
    key: "teamSizeMax",
    width: 80,
    render: (size) => <Text style={{ fontSize: "0.82rem" }}>{size || "—"}</Text>,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 110,
    render: (amt) => (
      <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
        ₹{amt ? (amt / 100).toFixed(0) : "0"}
      </Text>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (st) => {
      let statusLabel = st;
      if (st === "paid") {
        statusLabel = "PAID";
      } else if (st === "created") {
        statusLabel = "PENDING";
      }
      return (
        <Tag color={st === "paid" ? "success" : "warning"} style={{ borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>
          {statusLabel}
        </Tag>
      );
    },
  },
  {
    title: "Transaction Details",
    key: "details",
    render: (_, p) => (
      <Flex vertical gap={2} style={{ fontSize: "0.78rem", color: "#475569" }}>
        <div>
          <Text strong style={{ fontSize: "0.78rem" }}>
            Method:{" "}
          </Text>
          <Tag color="default" style={{ textTransform: "uppercase", fontSize: "0.72rem", margin: 0 }}>
            {p.paymentMethod || "N/A"}
          </Tag>
        </div>
        {p.razorpayOrderId && (
          <div>
            <Text type="secondary">Order: </Text>
            {p.razorpayOrderId}
          </div>
        )}
        {p.razorpayPaymentId && (
          <div>
            <Text type="secondary">Payment ID: </Text>
            <Text code style={{ color: "#7c3aed" }}>
              {p.razorpayPaymentId}
            </Text>
          </div>
        )}
        {(p.cardNetwork || p.cardLast4) && (
          <div>
            <Text type="secondary">Card: </Text>
            {p.cardNetwork || "Card"} ending in <b>{p.cardLast4 || "****"}</b>
          </div>
        )}
        {p.vpa && (
          <div>
            <Text type="secondary">UPI VPA: </Text>
            {p.vpa}
          </div>
        )}
        {p.bank && (
          <div>
            <Text type="secondary">Bank: </Text>
            {p.bank}
          </div>
        )}
      </Flex>
    ),
  },
];

function RequestsTabContent({
  requestsSubTab,
  onSubTabChange,
  pendingTrialRequests,
  pendingRmRequests,
  periodFilter,
  onPeriodFilterChange,
  requestColumns,
  rmRequestColumns,
  loading,
}) {
  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Segmented
          value={requestsSubTab}
          onChange={onSubTabChange}
          options={[
            {
              label: (
                <Flex align="center" gap={6}>
                  <span>Free Trial Requests</span>
                  <Badge
                    count={pendingTrialRequests.length}
                    overflowCount={99}
                    style={{ backgroundColor: "#7c3aed" }}
                  />
                </Flex>
              ),
              value: "trials",
            },
            {
              label: (
                <Flex align="center" gap={6}>
                  <span>RM Requests</span>
                  <Badge
                    count={pendingRmRequests.length}
                    overflowCount={99}
                    style={{ backgroundColor: "#f59e0b" }}
                  />
                </Flex>
              ),
              value: "rm",
            },
          ]}
        />

        <PeriodFilterDropdown value={periodFilter} onChange={onPeriodFilterChange} />
      </Flex>

      {requestsSubTab === "trials" ? (
        <Table
          columns={requestColumns}
          dataSource={pendingTrialRequests}
          rowKey={(r) => r._id || r.companyCode}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 880 }}
          size="middle"
          locale={{ emptyText: "No pending free trial requests found." }}
        />
      ) : (
        <Table
          columns={rmRequestColumns}
          dataSource={pendingRmRequests}
          rowKey={(r) => r._id || r.companyCode}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 880 }}
          size="middle"
          locale={{ emptyText: "No pending Relationship Manager requests found." }}
        />
      )}
    </Flex>
  );
}

function CompaniesTabContent({
  filteredCompanies,
  periodFilter,
  onPeriodFilterChange,
  onExportCsv,
  companyColumns,
  loading,
}) {
  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
          Registered Enterprise Tenants ({filteredCompanies.length})
        </Text>

        <Flex align="center" gap={12} wrap="wrap">
          <PeriodFilterDropdown value={periodFilter} onChange={onPeriodFilterChange} />
          <Button icon={<DownloadOutlined />} onClick={onExportCsv} style={{ borderRadius: 8 }}>
            Export Directory CSV
          </Button>
        </Flex>
      </Flex>

      <Table
        columns={companyColumns}
        dataSource={filteredCompanies}
        rowKey={(r) => r._id || r.companyCode}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 880 }}
        size="middle"
        locale={{ emptyText: "No companies found." }}
      />
    </Flex>
  );
}

function RmMgtTabContent({
  filteredCompanies,
  periodFilter,
  onPeriodFilterChange,
  rmColumns,
  loading,
}) {
  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
          Relationship Manager Allocations ({filteredCompanies.length})
        </Text>

        <PeriodFilterDropdown value={periodFilter} onChange={onPeriodFilterChange} />
      </Flex>

      <Table
        columns={rmColumns}
        dataSource={filteredCompanies}
        rowKey={(r) => r._id || r.companyCode}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 880 }}
        size="middle"
        locale={{ emptyText: "No companies found." }}
      />
    </Flex>
  );
}

function AssignRmModal({
  visible,
  company,
  form,
  submitting,
  onCancel,
  onSubmit,
}) {
  return (
    <Modal
      title={
        <Flex vertical gap={2}>
          <Text strong style={{ fontSize: "1.1rem" }}>
            Assign Relationship Manager
          </Text>
          {company && (
            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
              For company: {company.companyName} ({company.companyCode})
            </Text>
          )}
        </Flex>
      }
      open={visible}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Assign Manager"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter RM name." }]}>
          <Input placeholder="e.g. Rahul Sharma" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Phone" name="phone" rules={[{ required: true, message: "Please enter phone number." }]}>
              <Input prefix={<PhoneOutlined />} placeholder="+91 9876543210" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: "Please enter email." },
                { type: "email", message: "Please enter a valid email." },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="rahul@dealvoice.co" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Working Days" name="workingDays">
              <Input placeholder="Monday to Friday" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Working Hours" name="workingHours">
              <Input placeholder="10:00am to 7:00pm" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function CompanyPaymentsModal({
  visible,
  company,
  paymentsList,
  loading,
  onClose,
  columns,
}) {
  return (
    <Modal
      title={
        <Flex vertical gap={2}>
          <Text strong style={{ fontSize: "1.1rem" }}>
            Payment & Subscription History
          </Text>
          {company && (
            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
              Company: {company.companyName} ({company.companyCode})
            </Text>
          )}
        </Flex>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={850}
      destroyOnClose
    >
      <Table
        columns={columns}
        dataSource={paymentsList}
        rowKey={(p, idx) => p._id || p.razorpayPaymentId || idx}
        loading={loading}
        pagination={{ pageSize: 5 }}
        size="small"
        locale={{ emptyText: "No payments found for this company." }}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
}

const TAB_TITLES = {
  overview: "Platform Health & Metrics",
  requests: "Signups & Plan Requests",
  companies: "Tenant Organizations",
  payments: "Subscription Billing",
  rm: "Account Managers",
};

function SuperAdminDashboard({ onLogout, onReady }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [requestsSubTab, setRequestsSubTab] = useState("trials");
  const [searchQuery, setSearchQuery] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [collapsed, setCollapsed] = useState(false);

  // Company Dossier Drawer State
  const [dossierVisible, setDossierVisible] = useState(false);
  const [selectedCompanyDossierCode, setSelectedCompanyDossierCode] = useState(null);

  // RM Modal State
  const [rmModalVisible, setRmModalVisible] = useState(false);
  const [selectedCompanyForRm, setSelectedCompanyForRm] = useState(null);
  const [rmSubmitting, setRmSubmitting] = useState(false);
  const [rmForm] = Form.useForm();

  // Payments Modal State
  const [paymentsModalVisible, setPaymentsModalVisible] = useState(false);
  const [selectedCompanyForPayments, setSelectedCompanyForPayments] = useState(null);
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Fetch Companies
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminService.getCompanies();
      if (res?.success && Array.isArray(res.companies)) {
        setCompanies(res.companies);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
      message.error(err.message || "Failed to load company directory.");
    } finally {
      setLoading(false);
      if (onReady) {
        onReady();
      }
    }
  }, [onReady]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Approve Company
  const handleApproveCompany = useCallback(async (companyId) => {
    try {
      const res = await superAdminService.approveCompany(companyId);
      if (res?.success) {
        message.success("Successfully enabled 7-day trial for company!");
        fetchCompanies();
      }
    } catch (err) {
      message.error(err.message || "Failed to approve company trial.");
    }
  }, [fetchCompanies]);

  // Reject Company
  const handleRejectCompany = useCallback(async (companyId) => {
    try {
      const res = await superAdminService.rejectCompany(companyId);
      if (res?.success) {
        message.success("Company request rejected and account removed.");
        fetchCompanies();
      }
    } catch (err) {
      message.error(err.message || "Failed to reject company request.");
    }
  }, [fetchCompanies]);

  // Open RM Modal
  const handleOpenRmModal = useCallback((record) => {
    setSelectedCompanyForRm(record);
    const existingRm = record.relationshipManager || {};
    rmForm.setFieldsValue({
      name: existingRm.name || "",
      phone: existingRm.phone || "",
      email: existingRm.email || "",
      workingDays: existingRm.workingDays || "Monday to Friday",
      workingHours: existingRm.workingHours || "10:00am to 7:00pm",
    });
    setRmModalVisible(true);
  }, [rmForm]);

  // Submit RM Form
  const handleSubmitRm = async () => {
    try {
      const values = await rmForm.validateFields();
      setRmSubmitting(true);
      const res = await superAdminService.assignRelationshipManager(selectedCompanyForRm.companyCode, values);
      if (res?.success) {
        message.success("Relationship Manager assigned successfully!");
        setRmModalVisible(false);
        fetchCompanies();
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || "Failed to assign Relationship Manager.");
    } finally {
      setRmSubmitting(false);
    }
  };

  // Open Payments Modal
  const handleOpenPaymentsModal = useCallback(async (record) => {
    setSelectedCompanyForPayments(record);
    setPaymentsModalVisible(true);
    setPaymentsLoading(true);
    try {
      const res = await superAdminService.getCompanyPayments(record.companyCode);
      if (res?.success && Array.isArray(res.payments)) {
        setPaymentsList(res.payments);
      } else {
        setPaymentsList([]);
      }
    } catch (err) {
      message.error(err.message || "Failed to fetch payment history.");
      setPaymentsList([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  // Open Company Dossier Drawer
  const handleOpenDossier = useCallback((companyCode) => {
    setSelectedCompanyDossierCode(companyCode);
    setDossierVisible(true);
  }, []);

  // Filtered Companies
  const filteredCompanies = useMemo(() => {
    return companies.filter((c) => {
      if (!isDateInPeriod(c.createdAt, periodFilter)) {
        return false;
      }
      return matchesCompanySearch(c, searchQuery);
    });
  }, [companies, searchQuery, periodFilter]);

  // Pending Free Trial requests
  const pendingTrialRequests = useMemo(() => {
    return filteredCompanies.filter((c) => c.status === "Free-Trial-Request");
  }, [filteredCompanies]);

  // Pending RM requests (requested RM and has no RM assigned yet)
  const pendingRmRequests = useMemo(() => {
    return filteredCompanies.filter((c) => c.rmRequestTime && !c.relationshipManager?.name);
  }, [filteredCompanies]);

  // Auto-switch to RM requests if no trial requests but RM request exists
  useEffect(() => {
    if (activeTab === "requests" && pendingTrialRequests.length === 0 && pendingRmRequests.length > 0) {
      setRequestsSubTab("rm");
    }
  }, [activeTab, pendingTrialRequests.length, pendingRmRequests.length]);

  // General tenant lifecycle stats
  const stats = useMemo(() => {
    const total = companies.length;
    const pending = companies.filter((c) => c.status === "Free-Trial-Request").length;
    const active = companies.filter((c) => c.status === "Free-Trial").length;
    const paid = companies.filter((c) => c.status === "Paid" || c.status === "Active").length;
    const due = companies.filter((c) => c.status === "On due").length;
    return { total, pending, active, paid, due };
  }, [companies]);

  // Dedicated Relationship Manager stats
  const rmStats = useMemo(() => {
    const total = companies.length;
    const pending = companies.filter((c) => c.rmRequestTime && !c.relationshipManager?.name).length;
    const assigned = companies.filter((c) => c.relationshipManager?.name).length;
    const unassigned = companies.filter((c) => !c.relationshipManager?.name).length;
    return { total, pending, assigned, unassigned };
  }, [companies]);

  const requestColumns = useMemo(
    () =>
      buildRequestColumns({
        onOpenDossier: handleOpenDossier,
        onApproveCompany: handleApproveCompany,
        onRejectCompany: handleRejectCompany,
      }),
    [handleOpenDossier, handleApproveCompany, handleRejectCompany]
  );

  const companyColumns = useMemo(
    () =>
      buildCompanyColumns({
        onOpenDossier: handleOpenDossier,
        onOpenPaymentsModal: handleOpenPaymentsModal,
      }),
    [handleOpenDossier, handleOpenPaymentsModal]
  );

  const rmRequestColumns = useMemo(
    () =>
      buildRmRequestColumns({
        onOpenDossier: handleOpenDossier,
        onOpenRmModal: handleOpenRmModal,
      }),
    [handleOpenDossier, handleOpenRmModal]
  );

  const rmColumns = useMemo(
    () =>
      buildRmColumns({
        onOpenDossier: handleOpenDossier,
        onOpenRmModal: handleOpenRmModal,
      }),
    [handleOpenDossier, handleOpenRmModal]
  );

  const isDirectoryTab = activeTab === "requests" || activeTab === "companies" || activeTab === "rm";

  return (
    <Layout hasSider style={{ height: "100vh", overflow: "hidden" }}>
      {/* ── DEALVOICE LEFT SIDEBAR ── */}
      <Sidebar
        role="super_admin"
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab)}
        collapsed={collapsed}
        onCollapseChange={(col) => setCollapsed(col)}
      />

      {/* ── MAIN CONTENT LAYOUT ── */}
      <Layout style={{ height: "100vh", overflow: "hidden", background: "#f8fafc", display: "flex", flexDirection: "column", transition: "all 0.2s ease" }}>
        {/* Top Navbar */}
        <TopNavbar
          title={TAB_TITLES[activeTab] || "Super Admin"}
          activeTab={activeTab}
          onNavigateTab={(tab) => setActiveTab(tab)}
          onSearch={(val) => setSearchQuery(val)}
          onLogout={onLogout}
        />

        <Content style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto", width: "100%", flex: 1, overflowY: "auto" }}>
          {/* TAB 1: Platform Overview & Revenue Analytics */}
          {activeTab === "overview" && (
            <PlatformAnalyticsView onSelectCompany={(code) => handleOpenDossier(code)} />
          )}

          {/* TAB 4: Global Transactions & Ledger */}
          {activeTab === "payments" && (
            <GlobalPaymentsView onSelectCompany={(code) => handleOpenDossier(code)} />
          )}

          {/* TABS 2, 3, 5: Requests, Companies, and RM Management */}
          {isDirectoryTab && (
            <Spin spinning={loading} tip="Loading directory...">
              <Flex vertical gap={24}>
                {/* Top 4 Stats Cards */}
                <SuperAdminStatsStrip
                  activeTab={activeTab}
                  stats={stats}
                  rmStats={rmStats}
                  pendingTrialRequests={pendingTrialRequests}
                  pendingRmRequests={pendingRmRequests}
                />

                {/* Table Card */}
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    background: "#ffffff",
                  }}
                >
                  {activeTab === "requests" && (
                    <RequestsTabContent
                      requestsSubTab={requestsSubTab}
                      onSubTabChange={setRequestsSubTab}
                      pendingTrialRequests={pendingTrialRequests}
                      pendingRmRequests={pendingRmRequests}
                      periodFilter={periodFilter}
                      onPeriodFilterChange={setPeriodFilter}
                      requestColumns={requestColumns}
                      rmRequestColumns={rmRequestColumns}
                      loading={loading}
                    />
                  )}

                  {activeTab === "companies" && (
                    <CompaniesTabContent
                      filteredCompanies={filteredCompanies}
                      periodFilter={periodFilter}
                      onPeriodFilterChange={setPeriodFilter}
                      onExportCsv={() => exportCompaniesToCsv(filteredCompanies)}
                      companyColumns={companyColumns}
                      loading={loading}
                    />
                  )}

                  {activeTab === "rm" && (
                    <RmMgtTabContent
                      filteredCompanies={filteredCompanies}
                      periodFilter={periodFilter}
                      onPeriodFilterChange={setPeriodFilter}
                      rmColumns={rmColumns}
                      loading={loading}
                    />
                  )}
                </Card>
              </Flex>
            </Spin>
          )}
        </Content>

        <BottomNavigation
          role="super_admin"
          companies={companies}
          onNavigateTab={(tab) => setActiveTab(tab)}
        />
      </Layout>

      {/* Deep Company Dossier Drawer */}
      <CompanyDossierDrawer
        visible={dossierVisible}
        companyCode={selectedCompanyDossierCode}
        onClose={() => setDossierVisible(false)}
        onCompanyUpdated={fetchCompanies}
        onAssignRmClick={(company) => handleOpenRmModal(company)}
      />

      {/* Assign Relationship Manager Modal */}
      <AssignRmModal
        visible={rmModalVisible}
        company={selectedCompanyForRm}
        form={rmForm}
        submitting={rmSubmitting}
        onCancel={() => setRmModalVisible(false)}
        onSubmit={handleSubmitRm}
      />

      {/* Company Payment History Modal */}
      <CompanyPaymentsModal
        visible={paymentsModalVisible}
        company={selectedCompanyForPayments}
        paymentsList={paymentsList}
        loading={paymentsLoading}
        onClose={() => setPaymentsModalVisible(false)}
        columns={PAYMENT_COLUMNS}
      />
    </Layout>
  );
}

export default SuperAdminDashboard;
